import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Layers,
  ListChecks,
  ChevronRight,
  X,
  Store,
  Package,
} from "lucide-react";
import { getSyncLogs } from "../services/settingsApi";
import { catalogApi, storeApi } from "../services/api";
import { formatDateTime, formatTimeWithSeconds } from "../utils/dateFormatter";

const formatDuration = (startedAt, completedAt) => {
  if (!completedAt) {
    return "In progress";
  }

  const start = new Date(startedAt);
  const end = new Date(completedAt);
  const seconds = Math.max(0, Math.round((end - start) / 1000));

  if (seconds < 60) {
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return remainingSeconds === 0 ? `${minutes}m` : `${minutes}m ${remainingSeconds}s`;
};

const statusStyles = {
  success: {
    bg: "bg-green-50",
    text: "text-green-700",
    border: "border-green-200",
    icon: <CheckCircle size={18} className="text-green-600" />,
  },
  failed: {
    bg: "bg-red-50",
    text: "text-red-700",
    border: "border-red-200",
    icon: <XCircle size={18} className="text-red-600" />,
  },
  default: {
    bg: "bg-yellow-50",
    text: "text-yellow-700",
    border: "border-yellow-200",
    icon: <AlertCircle size={18} className="text-yellow-600" />,
  },
};

const formatSyncLabel = (value) => value?.replace(/_/g, " ") || "catalog sync";

const SyncCenter = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [nextSync, setNextSync] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState("");
  const [stores, setStores] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);

  const numberFormatter = useMemo(() => new Intl.NumberFormat(), []);

  const calculateNextSync = useCallback(() => {
    const now = new Date();
    const next = new Date();
    next.setHours(3, 0, 0, 0);

    if (now.getHours() >= 3) {
      next.setDate(next.getDate() + 1);
    }

    setNextSync(formatDateTime(next));
  }, []);

  const fetchLogs = useCallback(async () => {
    const response = await getSyncLogs();
    setLogs(response.data?.data || []);
  }, []);

  const fetchStores = useCallback(async () => {
    try {
      const response = await storeApi.getAll();
      setStores(response.data || []);
    } catch (err) {
      console.error("Failed to load stores", err);
    }
  }, []);

  const loadData = useCallback(
    async ({ showPageLoader = false } = {}) => {
      if (showPageLoader) {
        setLoading(true);
      }

      setError("");

      try {
        await Promise.all([fetchLogs(), fetchStores()]);
        setLastUpdatedAt(new Date().toISOString());
      } catch (err) {
        console.error("Failed to load sync data", err);
        setError("Unable to load sync data right now. Please try again.");
      } finally {
        if (showPageLoader) {
          setLoading(false);
        }
      }
    },
    [fetchLogs, fetchStores],
  );

  useEffect(() => {
    calculateNextSync();
    loadData({ showPageLoader: true });

    const interval = setInterval(() => {
      loadData();
    }, 30000);

    return () => clearInterval(interval);
  }, [calculateNextSync, loadData]);

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    setError("");
    try {
      await catalogApi.triggerManualSync();
      await loadData();
      calculateNextSync();
    } catch (err) {
      console.error("Failed to trigger sync:", err);
      let message = "Failed to sync catalog. Please try again.";
      const responseMessage = err?.response?.data?.message || err?.response?.data?.error;

      if (err?.code === "ECONNABORTED") {
        message = "Catalog sync is taking longer than expected. Please wait and check logs.";
      } else if (typeof responseMessage === "string" && responseMessage.trim().length > 0) {
        message = responseMessage;
      }

      setError(message);
    } finally {
      setIsRefreshing(false);
    }
  };

  const sortedLogs = useMemo(
    () =>
      [...logs].sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime()),
    [logs],
  );

  const metrics = useMemo(() => {
    if (sortedLogs.length === 0) {
      return {
        successCount: 0,
        failedCount: 0,
        runningCount: 0,
        totalRuns: 0,
        itemsSynced30d: 0,
        thirtyDayRuns: 0,
      };
    }

    const now = new Date();
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(now.getDate() - 30);

    let successCount = 0;
    let failedCount = 0;
    let runningCount = 0;
    let itemsSynced30d = 0;
    let thirtyDayRuns = 0;

    sortedLogs.forEach((log) => {
      if (log.status === "success") {
        successCount += 1;
      } else if (log.status === "failed") {
        failedCount += 1;
      } else {
        runningCount += 1;
      }

      if (log.started_at && new Date(log.started_at) >= thirtyDaysAgo) {
        itemsSynced30d += Number(log.items_synced) || 0;
        thirtyDayRuns += 1;
      }
    });

    return {
      successCount,
      failedCount,
      runningCount,
      totalRuns: sortedLogs.length,
      itemsSynced30d,
      thirtyDayRuns,
    };
  }, [sortedLogs]);

  const statsCardConfig = useMemo(
    () => [
      {
        label: "Total syncs",
        value: logs.length,
        helper: "All time",
        icon: <RefreshCw size={16} className="text-[#6b4226]" />,
      },
      {
        label: "Success rate",
        value: logs.length > 0 
          ? `${Math.round((metrics.successCount / logs.length) * 100)}%`
          : "—",
        helper: `${metrics.successCount} successful`,
        icon: <CheckCircle size={16} className="text-[#6b4226]" />,
      },
      {
        label: "Next sync",
        value: nextSync || "—",
        helper: "3:00 AM IST",
        icon: <Clock size={16} className="text-[#6b4226]" />,
      },
    ],
    [nextSync, logs.length, metrics.successCount, numberFormatter],
  );

  const lastSync = sortedLogs[0];

  const renderStatusBadge = (status, size = "md") => {
    const meta = statusStyles[status] || statusStyles.default;
    const sizeClasses = size === "sm" ? "text-[11px] px-2 py-0.5" : "text-xs px-2.5 py-0.5";
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full font-semibold capitalize ${meta.bg} ${meta.text} ${meta.border} ${sizeClasses}`}
      >
        {meta.icon}
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="animate-spin text-[#6b4226]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-bold text-gray-900">Sync Center</h2>
          <div className="flex flex-wrap items-center gap-3">
            {lastUpdatedAt && (
              <span className="text-xs text-gray-500">
                Updated {formatTimeWithSeconds(lastUpdatedAt)}
              </span>
            )}
            <button
              type="button"
              onClick={handleManualRefresh}
              className="px-4 py-2 rounded-lg border border-[#6b4226] bg-[#6b4226] text-white text-sm font-medium hover:bg-[#58341f] transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isRefreshing}
            >
              <RefreshCw size={16} className={isRefreshing ? "animate-spin" : ""} />
              {isRefreshing ? "Syncing..." : "Sync Now"}
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700 flex items-center gap-2">
            <AlertCircle size={18} />
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {statsCardConfig.map((card) => (
            <div
              key={card.label}
              className="rounded-lg border border-gray-100 bg-gradient-to-br from-white via-slate-50 to-slate-100 p-3 flex flex-col gap-1"
            >
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-gray-500">
                {card.icon}
                <span>{card.label}</span>
              </div>
              <p className="text-lg font-semibold text-gray-900">
                {typeof card.value === "number"
                  ? numberFormatter.format(card.value || 0)
                  : card.value}
              </p>
              {card.helper && <p className="text-xs text-gray-400 mt-0.5">{card.helper}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* Sync History List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="border-b border-gray-100 px-4 py-3">
          <div className="flex items-center gap-2">
            <ListChecks size={18} className="text-[#6b4226]" />
            <p className="text-sm font-semibold text-gray-900">Sync History</p>
            <span className="text-xs text-gray-500 ml-2">({sortedLogs.length} records)</span>
          </div>
        </div>

        {sortedLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center text-gray-500">
            <Clock size={36} className="text-gray-300 mb-2" />
            <p>No sync activity recorded yet</p>
            <p className="text-xs text-gray-400 mt-1">
              Click "Sync Now" to start your first catalog sync
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {sortedLogs.map((log) => (
              <div
                key={log.id}
                className="px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => setSelectedLog(log)}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {renderStatusBadge(log.status, "sm")}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900 text-sm">
                          {formatSyncLabel(log.sync_type)}
                        </p>
                        <span className="text-xs text-gray-400">•</span>
                        <p className="text-xs text-gray-500">
                          {formatDateTime(log.started_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-600">
                        <span>{numberFormatter.format(log.items_synced || 0)} items</span>
                        <span>•</span>
                        <span>{formatDuration(log.started_at, log.completed_at)}</span>
                        {log.summary?.stores_synced && (
                          <>
                            <span>•</span>
                            <span>{log.summary.stores_synced} stores</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-gray-400 flex-shrink-0" />
                </div>
                {(log.error || log.error_message) && (
                  <div className="mt-2 text-xs text-red-600 bg-red-50 rounded px-2 py-1">
                    <span className="font-semibold">Error:</span> {log.error || log.error_message}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detailed View Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Layers size={20} className="text-[#6b4226]" />
                <h3 className="text-lg font-bold text-gray-900">Sync Details</h3>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Status and Basic Info */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <div className="mt-1">{renderStatusBadge(selectedLog.status)}</div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Sync Type</p>
                  <p className="text-lg font-semibold text-gray-900 capitalize mt-1">
                    {formatSyncLabel(selectedLog.sync_type)}
                  </p>
                </div>
              </div>

              {/* Timing Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Started At</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {formatDateTime(selectedLog.started_at)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Completed At</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {selectedLog.completed_at ? formatDateTime(selectedLog.completed_at) : "In progress"}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Duration</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {formatDuration(selectedLog.started_at, selectedLog.completed_at)}
                  </p>
                </div>
              </div>

              {/* Summary Stats */}
              {selectedLog.summary && (
                <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Package size={16} className="text-[#6b4226]" />
                    Summary
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {selectedLog.summary.stores_synced !== undefined && (
                      <div>
                        <p className="text-xs text-gray-500">Stores Synced</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {selectedLog.summary.stores_synced}
                        </p>
                      </div>
                    )}
                    {selectedLog.summary.total_items_synced !== undefined && (
                      <div>
                        <p className="text-xs text-gray-500">Total Items</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {numberFormatter.format(selectedLog.summary.total_items_synced)}
                        </p>
                      </div>
                    )}
                    {selectedLog.summary.total_store_items_updated !== undefined && (
                      <div>
                        <p className="text-xs text-gray-500">Store Items Updated</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {numberFormatter.format(selectedLog.summary.total_store_items_updated)}
                        </p>
                      </div>
                    )}
                    {selectedLog.summary.duration_ms !== undefined && (
                      <div>
                        <p className="text-xs text-gray-500">Processing Time</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {(selectedLog.summary.duration_ms / 1000).toFixed(2)}s
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Store Results */}
              {selectedLog.summary?.store_results && Array.isArray(selectedLog.summary.store_results) && (
                <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Store size={16} className="text-[#6b4226]" />
                    Store Results ({selectedLog.summary.store_results.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedLog.summary.store_results.map((result, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-lg border ${
                          result.success
                            ? "bg-green-50 border-green-200"
                            : "bg-red-50 border-red-200"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {result.success ? (
                                <CheckCircle size={14} className="text-green-600" />
                              ) : (
                                <XCircle size={14} className="text-red-600" />
                              )}
                              <p className="font-medium text-sm text-gray-900">
                                {result.store_name}
                              </p>
                              <span className="text-xs text-gray-500">({result.branch_code})</span>
                            </div>
                            {result.success && (
                              <div className="mt-1 flex gap-3 text-xs text-gray-600">
                                <span>{numberFormatter.format(result.items_synced || 0)} items</span>
                                {result.store_items_updated !== undefined && (
                                  <>
                                    <span>•</span>
                                    <span>{numberFormatter.format(result.store_items_updated)} updated</span>
                                  </>
                                )}
                              </div>
                            )}
                            {!result.success && result.error && (
                              <p className="mt-1 text-xs text-red-600">{result.error}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Error Details */}
              {(selectedLog.error || selectedLog.error_message) && (
                <div className="border border-red-200 rounded-lg bg-red-50 p-4 space-y-2">
                  <h4 className="font-semibold text-red-900 flex items-center gap-2">
                    <AlertCircle size={16} className="text-red-600" />
                    Error Details
                  </h4>
                  <pre className="text-sm text-red-700 whitespace-pre-wrap font-mono bg-red-100 p-3 rounded overflow-x-auto">
                    {selectedLog.error || selectedLog.error_message}
                  </pre>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setSelectedLog(null)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncCenter;
